import {
  dataLayerPush,
  getProductInfo,
} from "@/components/ProductList/v3/analytics/analytics"
import {
  dataLayerHeartClickSaveListButton,
  dataLayerSavetolistGuest,
} from "@/components/SaveToList/analytics"
import {
  WISHLIST_DELETE,
  WISHLIST_SAVE,
} from "@/components/SaveToList/constant"
import { setAuthModalVisibility } from "@/store/features/authSlice"
import { addProduct, removeItems } from "@/store/features/favoritesSlice"
import { showToast } from "@/store/features/genericSlice"
import { useTranslation } from "next-i18next"
import { useDispatch } from "react-redux"

const useProductFavorites = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Function to get favorite icon status
  const getFavIconStatus = (
    isSaveProductToList,
    productSavedToList,
    userListItemsSkusRef,
    favoritesRef,
    skuCode,
    productDataId,
    isCurrentlyFavoriteOverride = null
  ) => {
    const inSavedItems = userListItemsSkusRef.current[skuCode]
    const isCurrentlyFavorite =
      isCurrentlyFavoriteOverride !== null
        ? isCurrentlyFavoriteOverride
        : favoritesRef.current?.some(fav => fav.productId === productDataId)

    let favIconStatus = false
    if (isSaveProductToList) {
      favIconStatus = productSavedToList || inSavedItems
    } else {
      favIconStatus = isCurrentlyFavorite
    }
    return favIconStatus
  }

  // click fav if not login then login
  const handleLogin = (
    e,
    cartData,
    propsProductCard,
    setLoginFavouriteCallback,
    locale,
    skuPrepend,
    general
  ) => {
    dataLayerSavetolistGuest(
      getProductInfo(
        "",
        true,
        false,
        cartData,
        propsProductCard,
        locale,
        skuPrepend,
        general
      )
    )
    window.loginFavouriteCallback = () => {
      setLoginFavouriteCallback(e)
    }
    dispatch(setAuthModalVisibility({ show: true }))
  }

  const handleCallBack = (
    status,
    message,
    isChecked,
    propsProductCard,
    index,
    cartData,
    locale,
    skuPrepend,
    general
  ) => {
    dataLayerPush(
      message,
      status,
      "favorite",
      isChecked,
      propsProductCard,
      index,
      cartData,
      locale,
      skuPrepend,
      general
    )
  }

  // Handle favorite icon click
  const handleFavorite = (
    e,
    {
      favoritesRef,
      userListItemsSkusRef,
      productDataId,
      masterSKU,
      setOpenModal,
      setDeleteFromList,
      setShowAddToList,
      setLoginFavouriteCallback,
      toasterContent,
      propsProductCard,
      cartData,
      isChecked,
      index,
      locale,
      skuPrepend,
      general,
      isSaveProductToList,
      isAuthenticated,
      skipDataLayer = false,
    } = {}
  ) => {
    if (!favoritesRef || !userListItemsSkusRef || !propsProductCard) {
      console.error("handleFavorite: Missing required parameters.", {
        favoritesRef,
        userListItemsSkusRef,
        propsProductCard,
      })
      return
    }
    const skuCode = propsProductCard.product.sku
    const currentFavorites = favoritesRef.current || []
    const currentUserListItemsSkus = userListItemsSkusRef.current || []
    const isCurrentlyFavorite = currentFavorites?.some(
      fav => fav.productId === productDataId
    )
    const productInfo = getProductInfo(
      "",
      true,
      false,
      cartData,
      propsProductCard,
      locale,
      skuPrepend,
      general
    )
    if (isSaveProductToList) {
      if (currentUserListItemsSkus[skuCode]) {
        setOpenModal(true)
        setDeleteFromList(true)
        if (!skipDataLayer) {
          dataLayerHeartClickSaveListButton(
            productInfo,
            "delete from list",
            WISHLIST_DELETE
          )
        }
        dataLayerHeartClickSaveListButton(
          productInfo,
          "delete from list",
          WISHLIST_DELETE
        )
        return
      }
      setShowAddToList(true)
      if (!skipDataLayer) {
        dataLayerHeartClickSaveListButton(
          productInfo,
          "save to list",
          WISHLIST_SAVE
        )
      }
      window.loginFavouriteCallback = null
      setLoginFavouriteCallback(null)
      return
    }

    if (isCurrentlyFavorite) {
      currentFavorites?.map(fav => {
        if (fav.productId === productDataId) {
          if (!isAuthenticated) {
            handleLogin(
              e,
              cartData,
              propsProductCard,
              setLoginFavouriteCallback,
              locale,
              skuPrepend,
              general
            )
          } else {
            dispatch(removeItems({ ids: [fav.id], isDataLayerNeeded: false }))
              .unwrap()
              .then(() => {
                if (!skipDataLayer) {
                  handleCallBack(
                    "success",
                    t("kf.favorites.removeItem"),
                    isChecked,
                    propsProductCard,
                    index,
                    cartData
                  )
                }
                dispatch(
                  showToast({
                    message:
                      toasterContent.unfavoriteSuccessMessage ||
                      t("kf.favorites.removeItem"),
                    isVisible: true,
                  })
                )
              })
              .catch(err => {
                dispatch(
                  showToast({
                    message: t("kf.favorites.removeItemError"),
                    isVisible: true,
                  })
                )
                if (!skipDataLayer) {
                  handleCallBack(
                    "failure",
                    t("kf.favorites.removeItemError"),
                    isChecked,
                    propsProductCard,
                    index,
                    cartData
                  )
                }
              })
          }
        }
      })
    } else {
      if (!isAuthenticated) {
        handleLogin(e, cartData, propsProductCard, setLoginFavouriteCallback)
      } else {
        dispatch(addProduct(skuCode))
          .unwrap()
          .then(() => {
            if (!skipDataLayer) {
              handleCallBack(
                "success",
                t("kf.favorites.success"),
                isChecked,
                propsProductCard,
                index,
                cartData
              )
            }
            dispatch(
              showToast({
                message:
                  toasterContent.successMessage || t("kf.favorites.success"),
                isVisible: true,
              })
            )
          })
          .catch(err => {
            dispatch(
              showToast({
                message: t("kf.favorites.error"),
                isVisible: true,
              })
            )
            if (!skipDataLayer) {
              handleCallBack(
                "failure",
                t("kf.favorites.error"),
                isChecked,
                propsProductCard,
                index,
                cartData
              )
            }
          })
      }
    }
    window.loginFavouriteCallback = null
    setLoginFavouriteCallback(null)
  }

  return {
    getFavIconStatus,
    handleFavorite,
    handleLogin,
  }
}

export default useProductFavorites
